import type { ProgramSlideLayoutV1 } from '../types/programContentSlideCanvas';

const SLIDE_RECENT_COLORS_KEY = 'cp:slide-recent-colors:v1';
const MAX_RECENT_COLORS = 10;
const MAX_SUGGESTION_COLORS = 12;

export function normalizeSlidePickerHex(raw: unknown, fallback = ''): string {
  if (typeof raw !== 'string') return fallback;
  const t = raw.trim();
  if (/^#[0-9a-f]{6}$/i.test(t)) return t.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(t)) {
    return `#${t[1]}${t[1]}${t[2]}${t[2]}${t[3]}${t[3]}`.toLowerCase();
  }
  return fallback;
}

function addHex(out: Set<string>, raw: unknown) {
  const n = normalizeSlidePickerHex(raw);
  if (n) out.add(n);
}

/** 현재 슬라이드 레이아웃에 실제로 보이는 텍스트·배경·테두리 색 */
export function collectSlideLayoutColors(layout: ProgramSlideLayoutV1): string[] {
  const out = new Set<string>();
  addHex(out, layout.pageBackgroundColorHex ?? layout.defaultBackgroundColorHex);
  addHex(out, layout.pageTextColorHex);
  for (const box of layout.boxes ?? []) {
    addHex(out, box.colorHex);
    for (const run of box.textRuns ?? []) addHex(out, run.colorHex);
  }
  for (const button of layout.buttons ?? []) {
    addHex(out, button.colorHex);
    addHex(out, button.backgroundColorHex);
  }
  for (const shape of layout.shapes ?? []) {
    addHex(out, shape.fillColorHex);
    addHex(out, shape.strokeColorHex);
    addHex(out, shape.colorHex);
  }
  for (const pl of layout.polylines ?? []) addHex(out, pl.strokeColorHex);
  for (const table of layout.tables ?? []) {
    addHex(out, table.borderColorHex);
    for (const cell of table.cells ?? []) {
      addHex(out, cell.colorHex);
      addHex(out, cell.backgroundColorHex);
    }
  }
  for (const quiz of layout.quizzes ?? []) {
    addHex(out, quiz.colorHex);
    addHex(out, quiz.backgroundColorHex);
  }
  for (const opt of layout.quizChoiceOptions ?? []) {
    addHex(out, opt.colorHex);
    addHex(out, opt.backgroundColorHex);
  }
  return Array.from(out);
}

export function readSlideRecentColors(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SLIDE_RECENT_COLORS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of parsed) {
      const n = normalizeSlidePickerHex(item);
      if (!n || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
      if (out.length >= MAX_RECENT_COLORS) break;
    }
    return out;
  } catch {
    return [];
  }
}

export function rememberSlideRecentColor(hex: string): string[] {
  const n = normalizeSlidePickerHex(hex);
  const prev = readSlideRecentColors().filter((c) => c !== n);
  const next = n ? [n, ...prev].slice(0, MAX_RECENT_COLORS) : prev;
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(SLIDE_RECENT_COLORS_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota */
    }
  }
  return next;
}

export function mergeSlideColorSuggestions(_recent: string[], used: string[], max = MAX_SUGGESTION_COLORS): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const hex of used) {
    const n = normalizeSlidePickerHex(hex);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
    if (out.length >= max) break;
  }
  return out;
}
